import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';

function OrderDetail() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [newOrderStatus, setNewOrderStatus] = useState('');
    const [newPaymentStatus, setNewPaymentStatus] = useState('');
    const [editedItems, setEditedItems] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [showItemModal, setShowItemModal] = useState(false);

    const fetchOrderDetails = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get(`/brand/orders/${id}`);
            if (data.success) {
                setOrder(data.data);
                setNewOrderStatus(data.data.orderStatus || '');
                setNewPaymentStatus(data.data.paymentStatus || '');
                const initial = {};
                (data.data.items || []).forEach((it, idx) => {
                    initial[idx] = {
                        orderItemID: it.orderItemID,
                        name: it.name,
                        variationName: it.variationName || '',
                        trackingCode: it.trackingCode || '',
                        deliveryCompany: it.deliveryCompany || '',
                        itemStatus: it.itemStatus || 'pending',
                        returnTrackingCode: it.returnTrackingCode || '',
                        returnDeliveryCompany: it.returnDeliveryCompany || '',
                        returnTrackingUrl: it.returnTrackingUrl || '',
                        returnStatus: it.returnStatus || 'none',
                        returnType: it.returnType || 'refund'
                    };
                });
                setEditedItems(initial);
            } else {
                setError('Order not found');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch order details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchOrderDetails();
        }
    }, [id, fetchOrderDetails]);

    // No order status update action (display only)

    // No payment status update action (display only)

    const handleChangeItem = (index, field, value) => {
        setEditedItems(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                [field]: value
            }
        }));
    };

    const handleSaveTracking = async () => {
        try {
            setUpdating(true);
            const payloadItems = Object.values(editedItems).map(it => ({
                orderItemID: it.orderItemID || undefined,
                name: it.name,
                variationName: it.variationName || undefined,
                trackingCode: it.trackingCode || null,
                deliveryCompany: it.deliveryCompany || null,
                itemStatus: it.itemStatus || undefined,
                returnTrackingCode: it.returnTrackingCode || null,
                returnDeliveryCompany: it.returnDeliveryCompany || null,
                returnTrackingUrl: it.returnTrackingUrl || null,
                returnStatus: it.returnStatus && it.returnStatus !== 'none' ? it.returnStatus : undefined,
                returnType: it.returnType || undefined
            }));
            const { data } = await axiosInstance.put(`/brand/orders/${id}/items-tracking`, { items: payloadItems });
            if (data.success) {
                setOrder(prev => ({
                    ...prev,
                    items: (prev.items || []).map((it, idx) => ({
                        ...it,
                        trackingCode: editedItems[idx]?.trackingCode || '',
                        deliveryCompany: editedItems[idx]?.deliveryCompany || '',
                        itemStatus: editedItems[idx]?.itemStatus || it.itemStatus,
                        returnTrackingCode: editedItems[idx]?.returnTrackingCode || '',
                        returnDeliveryCompany: editedItems[idx]?.returnDeliveryCompany || '',
                        returnTrackingUrl: editedItems[idx]?.returnTrackingUrl || '',
                        returnStatus: editedItems[idx]?.returnStatus || it.returnStatus || 'none',
                        returnType: editedItems[idx]?.returnType || it.returnType || 'refund'
                    }))
                }));
                alert('Tracking info saved');
            } else {
                alert('Failed to save tracking info');
            }
        } finally {
            setUpdating(false);
        }
    };

    const handleApproveReturn = async (orderItemID) => {
        if (!window.confirm('Are you sure you want to APPROVE this return/replacement request?')) return;
        try {
            setUpdating(true);
            const { data } = await axiosInstance.post(`/brand/orders/approve-return/${orderItemID}`);
            if (data.success) {
                alert(data.message || 'Return request approved');
                fetchOrderDetails();
            } else {
                alert(data.message || 'Failed to approve return');
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to approve return');
        } finally {
            setUpdating(false);
        }
    };

    const handleRejectReturn = async (orderItemID) => {
        if (!window.confirm('Are you sure you want to REJECT this return request?')) return;
        try {
            setUpdating(true);
            const { data } = await axiosInstance.post(`/brand/orders/reject-return/${orderItemID}`);
            if (data.success) {
                alert(data.message || 'Return request rejected');
                fetchOrderDetails();
            } else {
                alert(data.message || 'Failed to reject return');
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to reject return');
        } finally {
            setUpdating(false);
        }
    };

    const formatPrice = (price) => {
        return `₹${price?.toLocaleString('en-IN') || '0'}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'Preparing': 'bg-yellow-100 text-yellow-800',
            'Shipped': 'bg-blue-100 text-blue-800',
            'Delivered': 'bg-green-100 text-green-800',
            'Cancelled': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPaymentStatusColor = (status) => {
        const colors = {
            'successful': 'bg-green-100 text-green-800',
            'pending': 'bg-yellow-100 text-yellow-800',
            'failed': 'bg-red-100 text-red-800',
            'refunded': 'bg-purple-100 text-purple-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const safeParse = (value, fallback = null) => {
        try {
            if (typeof value !== 'string') return value || fallback;
            const parsed = JSON.parse(value);
            return (typeof parsed === 'string') ? safeParse(parsed, fallback) : parsed;
        } catch {
            return fallback;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-[var(--text)]">Loading order details...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-center">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Link to="/orders" className="text-blue-600 hover:text-blue-800">
                        ← Back to Orders
                    </Link>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-center">
                    <div className="text-[var(--text)] mb-4">Order not found</div>
                    <Link to="/orders" className="text-blue-600 hover:text-blue-800">
                        ← Back to Orders
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Link to="/orders" className="text-blue-600 hover:text-blue-800">
                            ← Back to Orders
                        </Link>
                        <h1 className="lux-heading text-2xl">Order #{order.orderID}</h1>
                    </div>
                </div>

                {order.shipping > 0 ? (
                    <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-center gap-3">
                        <span className="text-xl">🚚</span>
                        <div>
                            <p className="font-semibold text-green-800">Shipping Fee Acquired</p>
                            <p className="text-sm text-green-700">A shipping fee of {formatPrice(order.shipping)} was applied to your portion of this order.</p>
                        </div>
                    </div>
                ) : (
                    <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200 flex items-center gap-3">
                        <span className="text-xl">✨</span>
                        <div>
                            <p className="font-semibold text-gray-800">No Shipping Fee Acquired</p>
                            <p className="text-sm text-gray-600">Your brand did not acquire a shipping fee for this order (Order total &gt; ₹999, or your brand shipping rate is 0).</p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Order Information and Items */}
                    <div className="lg:col-span-3">
                        <div className="flex flex-col gap-6">
                            {/* Order Information */}
                            <div className="card p-6">
                                <h2 className="text-lg font-semibold mb-4">Order Information</h2>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-[var(--muted)]">Order ID</label>
                                        <div className="font-mono">#{order.orderID}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm text-[var(--muted)]">User ID</label>
                                        <div>{order.uid}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm text-[var(--muted)]">Payment Mode</label>
                                        <div>{order.paymentMode}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm text-[var(--muted)]">Total Amount</label>
                                        <div className="font-semibold">{formatPrice(order.total)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="card p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold">Order Items</h2>
                                    <button
                                        onClick={() => {
                                            console.log('Saving tracking payload', editedItems);
                                            handleSaveTracking();
                                        }}
                                        disabled={updating}
                                        className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {updating ? 'Saving...' : 'Save Tracking Info'}
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Product</th>
                                                <th className="text-center py-3 px-4 font-medium text-[var(--muted)]">Quantity</th>
                                                <th className="text-right py-3 px-4 font-medium text-[var(--muted)]">Price</th>
                                                <th className="text-right py-3 px-4 font-medium text-[var(--muted)]">Total</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Tracking Code</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Delivery Company</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Item Status</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Return Type</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Return Status</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Return AWB / Tracking</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Return Courier</th>
                                                <th className="text-left py-3 px-4 font-medium text-[var(--muted)]">Return Tracking Link</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.items?.map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <tr className="border-b">
                                                        <td className="py-4 px-4 text-left">
                                                            <div className="flex items-center space-x-3">
                                                                <img
                                                                    src={safeParse(item.featuredImage, [])?.[0]?.imgUrl || safeParse(item.featuredImage, [])?.[0] || '/placeholder-product.jpg'}
                                                                    alt={item.name}
                                                                    className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                                                    onClick={() => {
                                                                        setSelectedItem(item);
                                                                        setShowItemModal(true);
                                                                    }}
                                                                />
                                                                <div>
                                                                    <p 
                                                                        className="font-medium text-[var(--text)] cursor-pointer hover:text-blue-600 transition-colors"
                                                                        onClick={() => {
                                                                            setSelectedItem(item);
                                                                            setShowItemModal(true);
                                                                        }}
                                                                    >
                                                                        {item.name}
                                                                    </p>
                                                                    {item.variationName && (
                                                                        <p 
                                                                            className="text-sm text-[var(--muted)] cursor-pointer hover:text-blue-600 transition-colors"
                                                                            onClick={() => {
                                                                                setSelectedItem(item);
                                                                                setShowItemModal(true);
                                                                            }}
                                                                        >
                                                                            {item.variationName}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4 text-center">
                                                            <span className="bg-[var(--surface)] px-2 py-1 rounded text-sm">
                                                                {item.quantity}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-right">
                                                            {formatPrice(item.unitPriceAfter)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-medium">
                                                            {formatPrice(item.lineTotalAfter)}
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <input
                                                                type="text"
                                                                value={editedItems[index]?.trackingCode || ''}
                                                                onChange={(e) => handleChangeItem(index, 'trackingCode', e.target.value)}
                                                                placeholder="Tracking #"
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            />
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <select
                                                                value={editedItems[index]?.deliveryCompany || ''}
                                                                onChange={(e) => handleChangeItem(index, 'deliveryCompany', e.target.value)}
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            >
                                                                <option value="">Courier</option>
                                                                <option value="Delhivery">Delhivery</option>
                                                                <option value="BlueDart">Blue Dart</option>
                                                                <option value="EcomExpress">Ecom Express</option>
                                                                <option value="DTDC">DTDC</option>
                                                                <option value="Xpressbees">Xpressbees</option>
                                                                <option value="Shadowfax">Shadowfax</option>
                                                                <option value="IndiaPost">India Post</option>
                                                                <option value="Other">Other</option>
                                                            </select>
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <select
                                                                value={editedItems[index]?.itemStatus || 'pending'}
                                                                onChange={(e) => handleChangeItem(index, 'itemStatus', e.target.value)}
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            >
                                                                <option value="pending">Pending</option>
                                                                <option value="shipped">Shipped</option>
                                                                <option value="delivered">Delivered</option>
                                                            </select>
                                                        </td>
                                                        <td className="py-4 px-4 text-left align-top">
                                                            {/* Return Type Column */}
                                                            {item.returnStatus && item.returnStatus !== 'none' ? (
                                                                <div className="flex flex-col gap-1">
                                                                    <div className={`px-2 py-0.5 rounded text-[10px] font-black border shadow-sm w-fit text-center uppercase tracking-wider ${
                                                                        (editedItems[index]?.returnType || item.returnType) === 'replacement' ? 'bg-blue-600 text-white border-blue-700' :
                                                                        (editedItems[index]?.returnType || item.returnType) === 'refund' ? 'bg-purple-600 text-white border-purple-700' :
                                                                        'bg-gray-100 text-gray-500 border-gray-200'
                                                                    }`}>
                                                                        {editedItems[index]?.returnType || item.returnType || 'N/A'}
                                                                    </div>
                                                                    <select
                                                                        value={editedItems[index]?.returnType || item.returnType || 'refund'}
                                                                        onChange={(e) => handleChangeItem(index, 'returnType', e.target.value)}
                                                                        className="mt-1 w-full text-[9px] border rounded p-0.5 bg-[var(--surface)] font-bold uppercase transition-all shadow-sm"
                                                                    >
                                                                        <option value="refund">REFUND</option>
                                                                        <option value="replacement">REPLACEMENT</option>
                                                                    </select>
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-[var(--muted)]">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            {['return_approval', 'refund_approval', 'replacement_approval'].includes(item.returnStatus) ? (
                                                                <div className="flex flex-col gap-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-center">
                                                                    <p className="text-[10px] font-bold text-yellow-700 uppercase animate-pulse">Wait for Admin Approval</p>
                                                                    <div className="flex gap-1">
                                                                        <button 
                                                                            onClick={() => handleApproveReturn(item.orderItemID)}
                                                                            className="flex-1 bg-green-600 text-white text-[10px] py-1 px-1 rounded hover:bg-green-700 shadow-sm"
                                                                        >Approve</button>
                                                                        <button 
                                                                            onClick={() => handleRejectReturn(item.orderItemID)}
                                                                            className="flex-1 bg-red-600 text-white text-[10px] py-1 px-1 rounded hover:bg-red-700 shadow-sm"
                                                                        >Reject</button>
                                                                    </div>
                                                                </div>
                                                            ) : (item.returnStatus && item.returnStatus !== 'none' && item.returnStatus !== 'returnRejected') ? (
                                                                <div className="flex flex-col gap-2">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        {(editedItems[index]?.returnType || item.returnType) === 'replacement' && item.replacementOrderID && (
                                                                            <a href={`/orders/${item.replacementOrderID}`} className="text-[10px] text-blue-600 underline hover:text-blue-800 font-bold">
                                                                                VIEW NEW ORDER
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                     <select
                                                                        disabled={item.returnStatus === 'return_approval'}
                                                                        value={editedItems[index]?.returnStatus || item.returnStatus || 'return_initiated'}
                                                                        onChange={(e) => handleChangeItem(index, 'returnStatus', e.target.value)}
                                                                        className="w-full min-w-[140px] px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm font-bold uppercase transition-all shadow-sm"
                                                                    >
                                                                        <option value="return_initiated">Return initiated</option>
                                                                        <option value="return_picked">Return picked</option>
                                                                        <option value="returned">Returned (final)</option>
 
                                                                        {(editedItems[index]?.returnType || item.returnType) === 'replacement' ? (
                                                                            <>
                                                                                <option value="replacement_processing">Replacement processing</option>
                                                                                <option value="replacement_shipped">Replacement shipped</option>
                                                                                <option value="replacement_complete">Replacement complete</option>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <option value="refund_pending">Refund pending</option>
                                                                                <option value="refund_completed">Refund completed</option>
                                                                            </>
                                                                        )}
 
                                                                        <option value="returnRejected">Return Rejected</option>
                                                                    </select>
                                                                </div>
                                                            ) : item.returnStatus === 'returnRejected' ? (
                                                                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded border border-gray-200">Rejected</span>
                                                            ) : (
                                                                <span className="text-sm text-[var(--muted)]">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <input
                                                                type="text"
                                                                value={editedItems[index]?.returnTrackingCode || ''}
                                                                onChange={(e) => handleChangeItem(index, 'returnTrackingCode', e.target.value)}
                                                                placeholder="Return AWB"
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            />
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <select
                                                                value={editedItems[index]?.returnDeliveryCompany || ''}
                                                                onChange={(e) => handleChangeItem(index, 'returnDeliveryCompany', e.target.value)}
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            >
                                                                <option value="">Courier</option>
                                                                <option value="Delhivery">Delhivery</option>
                                                                <option value="BlueDart">Blue Dart</option>
                                                                <option value="EcomExpress">Ecom Express</option>
                                                                <option value="DTDC">DTDC</option>
                                                                <option value="Xpressbees">Xpressbees</option>
                                                                <option value="Shadowfax">Shadowfax</option>
                                                                <option value="IndiaPost">India Post</option>
                                                                <option value="Other">Other</option>
                                                            </select>
                                                        </td>
                                                        <td className="py-4 px-4 text-left">
                                                            <input
                                                                type="url"
                                                                value={editedItems[index]?.returnTrackingUrl || ''}
                                                                onChange={(e) => handleChangeItem(index, 'returnTrackingUrl', e.target.value)}
                                                                placeholder="Link"
                                                                className="w-full px-2 py-1 border border-[var(--border)] rounded bg-[var(--surface)] text-sm"
                                                            />
                                                        </td>
                                                    </tr>
                                                    {/* Return Request Details Row */}
                                                    {(item.returnStatus === 'return_approval' || item.returnReason) && (
                                                        <tr className="bg-gray-50 border-b">
                                                            <td colSpan={12} className="py-3 px-6">
                                                                <div className="flex flex-wrap gap-6 items-start">
                                                                    <div className="min-w-[150px]">
                                                                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Return Type</p>
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${item.returnType === 'refund' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                            {item.returnType === 'refund' ? 'Refund' : 'Replacement'}
                                                                        </span>
                                                                    </div>
                                                                    {item.returnReason && (
                                                                        <div className="flex-1 max-w-sm">
                                                                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Reason & Comments</p>
                                                                            <p className="text-sm font-medium text-gray-800">{item.returnReason}</p>
                                                                            {item.returnComments && <p className="text-xs text-gray-600 mt-1 italic">"{item.returnComments}"</p>}
                                                                        </div>
                                                                    )}
                                                                    {item.returnPhotos && (
                                                                        <div className="flex-1">
                                                                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Photos</p>
                                                                            <div className="flex gap-2 flex-wrap">
                                                                                {(typeof item.returnPhotos === 'string' ? JSON.parse(item.returnPhotos) : item.returnPhotos)?.map((url, i) => (
                                                                                    <a key={i} href={url} target="_blank" rel="noreferrer">
                                                                                        <img src={url} alt="return" className="w-12 h-12 rounded border object-cover hover:scale-110 transition-transform" />
                                                                                    </a>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {item.replacementOrderID && (
                                                                        <div className="min-w-[150px]">
                                                                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Replacement Order</p>
                                                                            <Link 
                                                                                to={`/orders/details/${item.replacementOrderID}`}
                                                                                className="text-blue-600 hover:text-blue-800 font-bold text-sm flex items-center gap-1"
                                                                            >
                                                                                View Order #{item.replacementOrderID} →
                                                                            </Link>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Order Summary */}
                                <div className="mt-6 border-t pt-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-[var(--muted)]">Item Total:</span>
                                            <span>{formatPrice(order.itemTotal || order.subtotal)}</span>
                                        </div>
                                        {order.discount > 0 && (
                                            <div className="flex justify-between text-green-600">
                                                <span>Discount:</span>
                                                <span>-{formatPrice(order.discount)}</span>
                                            </div>
                                        )}
                                        {order.discount > 0 && (
                                            <div className="flex justify-between">
                                                <span className="text-[var(--muted)]">Subtotal:</span>
                                                <span>{formatPrice(order.subtotal)}</span>
                                            </div>
                                        )}
                                        {order.couponDiscount > 0 && (
                                            <div className="flex justify-between text-green-600">
                                                <span>Coupon Discount ({order.couponCode}):</span>
                                                <span>-{formatPrice(order.couponDiscount)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-[var(--muted)]">Brand Shipping Fee:</span>
                                            <span>{formatPrice(order.shipping || 0)}</span>
                                        </div>
                                        <div className="flex justify-between text-lg font-semibold border-t border-[var(--border)] pt-2">
                                            <span>Customer Order Total:</span>
                                            <span>{formatPrice(order.total)}</span>
                                        </div>
                                        {order.overallShipping > 0 && order.overallShipping !== order.shipping && (
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>(Note: Customer paid {formatPrice(order.overallShipping)} total shipping across all items)</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Order Status */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Order Status</h2>
                            <div className="flex items-center gap-2">
                                <span className="text-purple-500">🚚</span>
                                <span className="font-medium">Current Status:</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                    {order.orderStatus || 'N/A'}
                                </span>
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Payment Status</h2>
                            <div className="flex items-center gap-2">
                                <span className="text-green-500">💳</span>
                                <span className="font-medium">Current Status:</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.paymentStatus)}`}>
                                    {order.paymentStatus || 'N/A'}
                                </span>
                            </div>
                        </div>

                        {/* Delivery Address */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Delivery Address</h2>
                            {order.deliveryAddress ? (
                                <div className="space-y-2">
                                    <div className="flex items-start gap-2">
                                        {/* <span className="text-blue-500 mt-1">📍</span> */}
                                        <div>
                                            <p className="font-medium">{order.deliveryAddress.line1}</p>
                                            {order.deliveryAddress.line2 && <p>{order.deliveryAddress.line2}</p>}
                                            <p>{order.deliveryAddress.city}, {order.deliveryAddress.state} {order.deliveryAddress.pincode}</p>
                                            {order.deliveryAddress.landmark && (
                                                <p className="text-sm text-[var(--muted)]">Near: {order.deliveryAddress.landmark}</p>
                                            )}
                                            <div className="mt-2 space-y-1">
                                                <p className="text-sm">
                                                    <span className="text-[var(--muted)]">Email:</span> {order.deliveryAddress.emailID}
                                                </p>
                                                <p className="text-sm">
                                                    <span className="text-[var(--muted)]">Phone:</span> {order.deliveryAddress.phoneNumber}
                                                </p>
                                                <p className="text-sm">
                                                    <span className="text-[var(--muted)]">Type:</span> {order.deliveryAddress.type}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-[var(--muted)]">Address not available</p>
                            )}
                        </div>

                        {/* Order Info */}
                        <div className="card p-6">
                            <h2 className="text-lg font-semibold mb-4">Order Details</h2>
                            <div className="space-y-2">
                                <div><strong>Order Date:</strong> {formatDate(order.createdAt)}</div>
                                <div><strong>User ID:</strong> {order.uid}</div>
                                <div><strong>Items:</strong> {order.items?.length || 0}</div>
                                <div><strong>Total:</strong> {formatPrice(order.total)}</div>
                            </div>
                        </div>
                </div>
            </div>

            {/* Variation/Detail Modal */}
            {showItemModal && selectedItem && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all scale-100 p-0">
                        {/* Header */}
                        <div className="p-4 border-b flex justify-between items-center bg-gray-50 sticky top-0 z-10 rounded-t-xl">
                            <h2 className="text-xl font-bold text-gray-800">Product Details</h2>
                            <button 
                                onClick={() => setShowItemModal(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Product Info */}
                            <div className="flex gap-6 mb-8 items-start border-b pb-6">
                                <img
                                    src={safeParse(selectedItem.featuredImage, [])?.[0]?.imgUrl || safeParse(selectedItem.featuredImage, [])?.[0] || '/placeholder-product.jpg'}
                                    alt={selectedItem.name}
                                    className="w-24 h-24 object-cover rounded-lg shadow-md border"
                                />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">{selectedItem.name}</h3>
                                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                                        <p className="text-sm text-gray-500">Quantity: <span className="text-gray-800 font-medium">{selectedItem.quantity}</span></p>
                                        <p className="text-sm text-gray-500">Price: <span className="text-gray-800 font-medium">{formatPrice(selectedItem.unitPriceAfter)}</span></p>
                                        <p className="text-sm text-gray-500">Line Total: <span className="text-blue-600 font-bold">{formatPrice(selectedItem.lineTotalAfter)}</span></p>
                                    </div>
                                    {selectedItem.comboItems && selectedItem.comboItems.length > 0 && (
                                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                                            <p className="text-xs text-green-700 font-black uppercase tracking-widest mb-2">Combo Product ({selectedItem.comboItems.length} items)</p>
                                            <div className="space-y-2">
                                                {selectedItem.comboItems.map((ci, idx) => (
                                                    <div key={idx} className="flex gap-2 items-center">
                                                        <img src={safeParse(ci.featuredImage, [])?.[0]?.imgUrl || safeParse(ci.featuredImage, [])?.[0] || '/placeholder-product.jpg'} className="w-8 h-8 object-cover rounded" alt="" />
                                                        <div className="text-[10px]">
                                                            <p className="font-bold text-gray-800 leading-none">{ci.name}</p>
                                                            {ci.variationName && <p className="text-gray-500 mt-1">{ci.variationName}</p>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Variations */}
                            {(selectedItem.fullVariationName || selectedItem.variationValues || (selectedItem.variationName && !selectedItem.comboID)) && (
                                <div className="mb-8">
                                    <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-600 mb-4 flex items-center gap-2">
                                        <span className="w-8 h-[1px] bg-blue-200"></span>
                                        Variation Details
                                    </h4>
                                    <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100 shadow-sm">
                                        <p className="text-base font-black text-blue-900 mb-4">{selectedItem.variationName || selectedItem.fullVariationName}</p>
                                        
                                        {safeParse(selectedItem.variationValues, [])?.length > 0 && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                                {safeParse(selectedItem.variationValues, []).map((attr, idx) => 
                                                    Object.entries(attr).map(([key, value]) => (
                                                        <div key={`${idx}-${key}`} className="bg-white p-3 rounded-lg border border-blue-100 flex flex-col gap-1">
                                                            <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none">{key}</span>
                                                            <span className="text-sm font-bold text-blue-800">{value}</span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        
                                        {(selectedItem.variationPrice || selectedItem.variationStock !== undefined) && (
                                            <div className="mt-5 pt-5 border-t border-blue-100 grid grid-cols-2 gap-4">
                                                {selectedItem.variationPrice && (
                                                    <div>
                                                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">Base Price</p>
                                                        <p className="text-lg font-black text-blue-900">{formatPrice(selectedItem.variationPrice)}</p>
                                                    </div>
                                                )}
                                                {selectedItem.variationStock !== undefined && (
                                                    <div>
                                                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">Current Stock</p>
                                                        <p className="text-lg font-black text-blue-900">{selectedItem.variationStock}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Custom Inputs */}
                            {selectedItem.custom_inputs && safeParse(selectedItem.custom_inputs, null) !== null && Object.keys(safeParse(selectedItem.custom_inputs, {})).length > 0 && (
                                <div className="mb-2">
                                    <h4 className="text-xs font-black uppercase tracking-[0.2em] text-purple-600 mb-4 flex items-center gap-2">
                                        <span className="w-8 h-[1px] bg-purple-200"></span>
                                        Customization Details
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {Object.entries(safeParse(selectedItem.custom_inputs, {})).map(([id, value]) => {
                                            // Find field name if definitions are available
                                            const fieldDef = safeParse(selectedItem.productCustomInputs, []).find(f => f.id === id);
                                            const label = fieldDef?.fieldName || id;
                                            return (
                                                <div key={id} className="bg-purple-50 p-4 rounded-xl border border-purple-100 shadow-sm flex flex-col gap-1">
                                                    <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest leading-none">{label}</span>
                                                    <span className="text-sm font-bold text-purple-800 break-words">{String(value)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-gray-50 border-t text-center">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Ithyaraa Order Management</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
);
}

export default OrderDetail;
